import { createClient, createConfig } from "./_internal/client/index";
import type { Client } from "./_internal/client/index";

/** SDK version and default API version — sourced from generated version.ts */
import { SDK_VERSION, DEFAULT_API_VERSION } from "./version";
export { SDK_VERSION, DEFAULT_API_VERSION };

export interface BaseClientConfig {
  /** Base URL of the GPT Platform API */
  baseUrl?: string;
  /** User JWT token (Bearer auth) */
  token?: string;
  /** Application API key (x-application-key header) */
  apiKey?: string;
  /**
   * Application UUID for use in API call body attributes (e.g., application_id
   * when creating plans, agents, or other application-scoped resources).
   * NOT sent as a header — the server identifies the application from the API key.
   */
  applicationId?: string;
  /**
   * API version date to use for requests (e.g., "2025-12-03").
   * Defaults to the version this SDK was built against.
   * Pin this to a specific date to prevent breaking changes
   * when upgrading the SDK.
   */
  apiVersion?: string;
  /**
   * Default workspace ID appended as `?workspace_id=` to every request.
   * Use for server-side operations scoped to a specific workspace.
   */
  workspaceId?: string;
}

export interface RequestOptions {
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
  /** Idempotency key override */
  idempotencyKey?: string;
  /** Additional headers for this request */
  headers?: Record<string, string>;
}

/**
 * Check if a URL is considered secure for transmitting credentials.
 * Localhost and 127.0.0.1 are allowed for development.
 */
function isSecureUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:") return true;
    if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1")
      return true;
    return false;
  } catch {
    return false;
  }
}

export abstract class BaseClient {
  protected config: BaseClientConfig;

  /** Per-instance HTTP client — isolated from all other BaseClient instances */
  protected clientInstance: Client;

  /** The effective API version used by this client instance */
  public readonly apiVersion: string;

  constructor(config: BaseClientConfig = {}) {
    this.config = config;
    this.apiVersion = config.apiVersion ?? DEFAULT_API_VERSION;

    // Security: Warn if using non-HTTPS URL in non-localhost environment
    if (config.baseUrl && !isSecureUrl(config.baseUrl)) {
      console.warn(
        "[GPT Platform SDK] Warning: Using non-HTTPS URL. " +
          "Credentials may be transmitted insecurely. " +
          "Use HTTPS in production environments.",
      );
    }

    // Create an isolated client instance — not the module-level singleton.
    // This prevents multiple GptAdmin instances from sharing interceptor lists
    // or overwriting each other's baseUrl configuration.
    const clientConfig: Record<string, unknown> = {};
    if (config.baseUrl) clientConfig["baseUrl"] = config.baseUrl;

    this.clientInstance = createClient(createConfig(clientConfig));

    this.clientInstance.interceptors.request.use((req) => {
      // Security: Verify HTTPS before attaching credentials
      const requestUrl = req.url || config.baseUrl || "";
      if ((config.apiKey || config.token) && !isSecureUrl(requestUrl)) {
        console.warn(
          "[GPT Platform SDK] Warning: Sending credentials over non-HTTPS connection.",
        );
      }

      req.headers.set(
        "Accept",
        `application/vnd.api+json; version=${this.apiVersion}`,
      );
      req.headers.set("Content-Type", "application/vnd.api+json");

      if (config.apiKey) {
        req.headers.set("x-application-key", config.apiKey);
      }
      if (config.token) {
        req.headers.set("Authorization", `Bearer ${config.token}`);
      }

      // Workspace ID query parameter
      if (this.config.workspaceId) {
        const url = new URL(req.url);
        if (!url.searchParams.has("workspace_id")) {
          url.searchParams.set("workspace_id", this.config.workspaceId);
          req = new Request(url.toString(), req);
        }
      }

      return req;
    });
  }

  protected async requestWithRetry<T>(fn: () => Promise<T>): Promise<T> {
    return fn();
  }

  protected unwrap<T>(resource: unknown): T {
    if (!resource) return null as T;
    const obj = resource as Record<string, unknown>;

    // Unwrap action-result envelope from the NormalizeJsonApiResponse plug:
    // { type: "action-result", attributes: { ... } } → { ... }
    if (
      obj.type === "action-result" &&
      obj.attributes &&
      typeof obj.attributes === "object"
    ) {
      return obj.attributes as T;
    }

    // If the resource is wrapped in { data: ... }, extract it.
    if (obj.data && !obj.id && !obj.type) {
      return this.unwrap<T>(obj.data as T);
    }
    return resource as T;
  }

  protected getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.config.apiKey) {
      headers["x-application-key"] = this.config.apiKey;
    }
    if (this.config.token) {
      headers["Authorization"] = `Bearer ${this.config.token}`;
    }
    return headers;
  }

  /**
   * Switch the workspace context for all subsequent requests.
   */
  public setWorkspaceId(workspaceId: string | undefined): void {
    this.config = { ...this.config, workspaceId };
  }

  /**
   * Get the current workspace ID, if set.
   */
  public getWorkspaceId(): string | undefined {
    return this.config.workspaceId;
  }
}
